<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, type PropType, provide, ref } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import { $t } from '@previo/core'
import { PDateRangePickerFastChoice } from '../date-range-picker/types'
import PSpace from '../space/PSpace.vue'
import { PSpaceSize } from '../space/types'
import PTimeInputField from '../fields/date/PTimeInputField.vue'
import { PTimeInputFieldIncrement } from '../fields/types'
import { usePrevioDS } from '../../composables/helpers'
import { type Emits } from './types'
import { DateTimeRangeSelectionService } from './services/DateTimeRangeSelectionService'
import { DateTimeRange } from './model/DateTimeRange'
import Carousel from './components/Carousel.vue'
import FastChoicesSelect from './components/fast-choices/FastChoicesSelect.vue'
import FastChoicesList from './components/fast-choices/FastChoicesList.vue'
import { provideDateTimeRangePickerState } from './composables/DateTimeRangePickerState'
import HeaderNavigation from './components/navigation/Navigation.vue'

const props = defineProps({
  fastChoices: {
    type: Array as PropType<PDateRangePickerFastChoice[]>,
    default: () => [],
  },
  min: {
    type: Object as PropType<Dayjs>,
    required: false,
  },
  max: {
    type: Object as PropType<Dayjs>,
    required: false,
  },
  maxRangeDays: {
    type: Number,
    required: false,
  },
  timeStrict: {
    type: Boolean,
    default: false,
  },
  timeIncrement: {
    type: Number as PropType<PTimeInputFieldIncrement>,
    default: PTimeInputFieldIncrement.QUARTER,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  disableClearable: {
    type: Boolean,
    default: false,
  },
  hideTimeField: {
    type: Boolean,
    default: false,
  },
  defaultTimeFrom: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) {
        return true
      }

      return dayjs(value, 'HH:mm', true).isValid()
    },
  },
  defaultTimeTo: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) {
        return true
      }

      return dayjs(value, 'HH:mm', true).isValid()
    },
  },
})

const emits = defineEmits<Emits>()

const model = defineModel<DateTimeRange>({ default: new DateTimeRange() })

const ds = usePrevioDS()
const carouselComponent = ref<InstanceType<typeof Carousel>>()
const navigation = provideDateTimeRangePickerState()
const calendarWrapperComponent = ref<HTMLDivElement>()
const fastChoiceHeight = ref<string>('auto')
let resizeObserver: ResizeObserver | null = null

const hasFastChoices = computed<boolean>(() => props.fastChoices?.length > 0)

const onSubtract = (): void => {
  carouselComponent.value?.subtractMonth()
}

const onAdvance = (): void => {
  carouselComponent.value?.advanceMonth()
}

const selectionService = new DateTimeRangeSelectionService(
  navigation,
  emits,
  props.defaultTimeFrom,
  props.defaultTimeTo,
)

provide('dateTimeRangeSelectionService', selectionService)

const setSelectingFrom = (): void => {
  navigation.setSelectingFrom()
}

const setSelectingTo = (): void => {
  navigation.setSelectingTo()
}

const updateFastChoiceComponentHeight = () => {
  if (calendarWrapperComponent.value && ds.screen().isLaptopAndLarge) {
    const height = calendarWrapperComponent.value.offsetHeight
    fastChoiceHeight.value = `${height}px`
  }
}

onMounted(() => {
  if (props.min) {
    navigation.setMin(props.min)
  }

  if (props.max) {
    navigation.setMax(props.max)
  }

  if (props.maxRangeDays !== undefined) {
    navigation.setMaxRangeDays(props.maxRangeDays)
  }

  navigation.setDisableClearable(props.disableClearable)

  if (model.value) {
    navigation.setRange(model.value)
  }

  // Set up ResizeObserver to track calendarWrapperComponent height
  nextTick(() => {
    if (calendarWrapperComponent.value) {
      resizeObserver = new ResizeObserver(() => {
        updateFastChoiceComponentHeight()
      })
      resizeObserver.observe(calendarWrapperComponent.value)
      updateFastChoiceComponentHeight()
    }
  })
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})

defineExpose({
  setSelectingFrom,
  setSelectingTo,
})
</script>

<template>
  <div
    :class="[
      'ds-date-time-range-picker',
      { 'p-flex p-no-wrap p-items-start': ds.screen().isLaptopAndLarge },
    ]"
  >
    <div ref="calendarWrapperComponent" class="p-flex p-flex-direction-column p-no-wrap">
      <fast-choices-select
        v-model="model"
        :fast-choices="fastChoices"
        :default-time-from="defaultTimeFrom"
        :default-time-to="defaultTimeTo"
      />

      <p-space v-if="hasFastChoices" :mobile="PSpaceSize.MD" :laptop="PSpaceSize.ZERO" vertical />

      <header-navigation @subtract="onSubtract" @advance="onAdvance" />

      <carousel
        ref="carouselComponent"
        v-model="model"
        :default-time-from="defaultTimeFrom"
        :default-time-to="defaultTimeTo"
      />

      <p-space :mobile="PSpaceSize.SM" vertical />

      <template v-if="!hideTimeField">
        <div class="p-flex">
          <div class="p-flex-1">
            <p-time-input-field
              v-model="model.from"
              :label="$t('Time from')"
              :min="min"
              :max="max"
              :disable-clearable="disableClearable"
              :increment="timeIncrement"
              :strict="timeStrict"
              :disable="disable"
              cy="date-time-range-picker-input-field-from"
            />
          </div>

          <p-space :mobile="PSpaceSize.SM" />

          <div class="p-flex-1">
            <p-time-input-field
              v-model="model.to"
              :label="$t('Time to')"
              :min="min"
              :max="max"
              :disable-clearable="disableClearable"
              :increment="timeIncrement"
              :strict="timeStrict"
              :disable="disable"
              cy="date-time-range-picker-input-field-to"
            />
          </div>
        </div>
      </template>
    </div>

    <p-space v-if="hasFastChoices" :mobile="PSpaceSize.ZERO" :laptop="PSpaceSize.SM" />

    <fast-choices-list
      v-if="ds.screen().isLaptopAndLarge"
      v-model="model"
      :fast-choices="fastChoices"
      :default-time-from="defaultTimeFrom"
      :default-time-to="defaultTimeTo"
      class="p-display-flex"
      :style="{
        height: fastChoiceHeight,
      }"
    />
  </div>
</template>
